import { apiClient, assetApi } from "shared/api/client/orval";
import {
  getAssetById,
  removeAsset,
} from "shared/api/client/orvalHelpers";

export async function createAssetUpload(): Promise<string> {
  const res = (await (assetApi as any).postApiAssetCreate()).data;
  const id = res?.assetUploadId ?? res?.id;
  if (!id) {
    throw new Error("Asset create did not return an assetUploadId.");
  }
  return String(id);
}

export async function saveAssetDraft(payload: unknown): Promise<void> {
  await assetApi.postApiAssetSaveDraft(payload as any);
}

export async function submitAssetForPublishing(payload: unknown): Promise<void> {
  await assetApi.postApiAssetSubmit(payload as any);
}

export async function editPublishedAsset(
  assetId: number,
  payload: unknown
): Promise<void> {
  await assetApi.putApiAssetEditAsetAssetId(assetId, payload as any);
}

export async function changeSubtitleLanguage(
  subtitleId: number,
  newLanguage: string
): Promise<void> {
  await assetApi.postApiAssetChangeSubtitleLanguageSubtitleId(subtitleId, {
    newLanguage,
  } as any);
}

export async function getUploadAssetById(
  assetId: string | number
): Promise<any> {
  return getAssetById(assetId);
}

export async function removeUploadAsset(
  assetId: string | number
): Promise<any> {
  return removeAsset(assetId);
}

/**
 * Runs the server's ffprobe inspection of the uploaded master. The endpoint
 * takes no metadata body and returns a nested probe result, including isValid.
 * Unavailable probes may be skipped, but an explicit rejection blocks submission.
 */
export async function postVideoProbe(
  videoId: string | number
): Promise<boolean> {
  let res;
  try {
    res = await apiClient.post(
      `/api/Asset/upload/probe/${encodeURIComponent(String(videoId))}`
    );
  } catch (err) {
    const response = (err as { response?: { data?: { probe?: { isValid?: boolean } } } })?.response;
    if (response?.data?.probe?.isValid === false) {
      res = response;
    } else {
      console.warn("Video probe report skipped:", err);
      return false;
    }
  }
  const probe = res.data?.probe;
  if (probe?.isValid === false) {
    const errors = Array.isArray(probe.errors) ? probe.errors.join("; ") : probe.errors;
    throw new Error(probe.message || errors || res.data?.message || "The uploaded video failed server validation.");
  }
  return probe?.isValid === true;
}

export type TranscodeState =
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "unknown";

export type TranscodeStatus = {
  state: TranscodeState;
  /** Raw status string reported by the backend. */
  rawStatus: string;
  /** 0..100 when the backend reports it. */
  progress: number | null;
  message: string | null;
};

export function normalizeTranscodeStatus(data: unknown): TranscodeStatus {
  const o = (data && typeof data === "object" ? data : {}) as Record<
    string,
    unknown
  >;
  const rawStatus = String(
    o.status ?? o.state ?? o.videoStatus ?? (typeof data === "string" ? data : "")
  );
  const key = rawStatus.trim().toLowerCase();

  let state: TranscodeState = "unknown";
  if (key === "queued" || key === "pending" || key === "waiting" || key === "uploaded" || key === "pendingupload") {
    state = "queued";
  } else if (
    key === "processing" ||
    key === "encoding" ||
    key === "transcoding" ||
    key === "inprogress" ||
    key === "in_progress"
  ) {
    state = "processing";
  } else if (
    key === "completed" ||
    key === "complete" ||
    key === "ready" ||
    key === "done" ||
    key === "succeeded" ||
    key === "success"
  ) {
    state = "completed";
  } else if (key === "failed" || key === "error" || key === "rejected") {
    state = "failed";
  }

  const rawProgress =
    o.progress ?? o.progressPercent ?? o.percent ?? o.percentage;
  const num = typeof rawProgress === "string" ? Number(rawProgress) : rawProgress;
  const progress =
    typeof num === "number" && Number.isFinite(num)
      ? Math.max(0, Math.min(100, Math.round(num)))
      : null;

  const msg = o.message ?? o.error ?? o.errorMessage;

  return {
    state,
    rawStatus,
    progress,
    message: typeof msg === "string" && msg ? msg : null,
  };
}

export async function getTranscodeStatus(
  videoId: string | number
): Promise<TranscodeStatus> {
  const res = await apiClient.get(
    `/api/Asset/upload/transcode-status/${encodeURIComponent(String(videoId))}`
  );
  return normalizeTranscodeStatus(res.data);
}

export function isTranscodeActive(state: TranscodeState): boolean {
  return state === "queued" || state === "processing";
}
